import { Injectable, computed, effect, inject, signal, untracked } from '@angular/core';
import { EMPTY, Observable, map, of, shareReplay, tap } from 'rxjs';
import { AcademicTitle } from '../../enums/academic-title.enum';
import { ApplicantSkillLevel } from '../../enums/applicant-skill-level.enum';
import { Gender } from '../../enums/gender.enum';
import { PlatformDocumentKind } from '../enums/platform-document-kind.enum';
import { PlatformImportedCvAction } from '../enums/platform-imported-cv-action.enum';
import { PlatformMissingDataType } from '../enums/platform-missing-data-type.enum';
import {
  PlatformApiProfile,
  PlatformDocument,
  PlatformImportedCv,
  PlatformProfile,
  PlatformProfileSkill,
  PlatformProfileUpdate,
} from '../models/platform.models';
import {
  startDateFromAvailability,
  startDateFromIso,
} from '../utils/platform-availability.util';
import { PlatformAuthService } from './platform-auth.service';
import { PlatformApiImportedCv, PlatformCandidateApiService } from './platform-candidate-api.service';

@Injectable({
  providedIn: 'root',
})
export class PlatformProfileService {
  private readonly auth = inject(PlatformAuthService);
  private readonly api = inject(PlatformCandidateApiService);

  private readonly apiProfileState = signal<PlatformApiProfile | null>(null);
  private readonly pendingImportedCvsState = signal<PlatformImportedCv[]>([]);
  private profileRequest: Observable<PlatformApiProfile> | null = null;

  readonly profile = computed(() => {
    const apiProfile = this.apiProfileState();
    const email = this.auth.session()?.email ?? '';
    if (!apiProfile) {
      return this.emptyProfile(email);
    }
    return this.toUiProfile(apiProfile, this.pendingImportedCvsState());
  });

  readonly profileLoaded = computed(() => this.apiProfileState() !== null);

  readonly completenessPercent = computed(() => {
    const profile = this.profile();
    const checks = [
      Boolean(profile.firstName && profile.lastName),
      Boolean(profile.currentOccupation),
      Boolean(profile.phone),
      Boolean(profile.city),
      Boolean(profile.photoSrc),
      profile.preferredLocations.length > 0,
      Boolean(profile.workingModel),
      profile.salaryMin != null || profile.salaryMax != null,
      Boolean(profile.availability),
      Boolean(profile.linkedin || profile.xing),
      profile.documents.some((document) => document.kind === PlatformDocumentKind.MAIN_CV),
      profile.skills.length > 0,
      profile.languages.length > 0,
    ];
    const filled = checks.filter(Boolean).length;
    return Math.round((filled / checks.length) * 100);
  });

  readonly showProfileCompleteness = computed(
    () => this.profileLoaded() && this.completenessPercent() < 80
  );

  constructor() {
    effect(() => {
      const me = this.auth.session();
      untracked(() => {
        if (!me) {
          this.apiProfileState.set(null);
          this.pendingImportedCvsState.set([]);
          this.profileRequest = null;
          return;
        }
        this.ensureLoaded().subscribe({ error: () => undefined });
        this.reloadImportedCvs();
      });
    });
  }

  /**
   * Loads the full profile once per session (shared in-flight request).
   * @returns Observable of the API profile
   */
  ensureLoaded(): Observable<PlatformApiProfile> {
    if (!this.auth.loggedIn()) {
      return EMPTY;
    }
    const cached = this.apiProfileState();
    if (cached) {
      return of(cached);
    }
    if (!this.profileRequest) {
      this.profileRequest = this.api.getProfile().pipe(
        tap((profile) => this.applyApiProfile(profile)),
        shareReplay({ bufferSize: 1, refCount: false }),
        tap({
          error: () => {
            this.profileRequest = null;
          },
        })
      );
    }
    return this.profileRequest;
  }

  /**
   * Forces a profile reload from the API.
   * @returns Observable of the API profile
   */
  reload(): Observable<PlatformApiProfile> {
    this.profileRequest = null;
    this.profileRequest = this.api.getProfile().pipe(
      tap((profile) => this.applyApiProfile(profile)),
      shareReplay({ bufferSize: 1, refCount: false }),
      tap({
        error: () => {
          this.profileRequest = null;
        },
      })
    );
    return this.profileRequest;
  }

  /**
   * Saves profile fields and applies imported-CV decisions.
   * @param profile - Profile snapshot from the form
   * @returns Observable of the updated profile
   */
  save(profile: PlatformProfile): Observable<PlatformApiProfile> {
    const body: PlatformProfileUpdate = {
      firstName: profile.firstName,
      lastName: profile.lastName,
      title: profile.title,
      academicTitle: profile.academicTitle,
      currentOccupation: profile.currentOccupation || null,
      phone: profile.phone || null,
      city: profile.city || null,
      preferredLocations: profile.preferredLocations,
      workingModel: profile.workingModel,
      salaryMin: profile.salaryMin,
      salaryMax: profile.salaryMax,
      currency: profile.currency || null,
      availability: profile.availability,
      linkedin: profile.linkedin || null,
      xing: profile.xing || null,
      skills: profile.skills,
      languages: profile.languages,
    };
    const uploads = this.adoptPayload(profile.pendingImportedCvs);
    const request =
      uploads.length > 0
        ? this.api.adoptDocuments(uploads).pipe(
            tap((apiProfile) => this.applyApiProfile(apiProfile)),
            tap(() =>
              this.pendingImportedCvsState.set(
                profile.pendingImportedCvs.filter(
                  (cv) => cv.action === PlatformImportedCvAction.UNSET
                )
              )
            )
          )
        : this.api.updateProfile(body);
    return request.pipe(
      tap((apiProfile) => this.applyApiProfile(apiProfile)),
      tap(() => {
        if (uploads.length > 0) {
          this.api.updateProfile(body).subscribe({
            next: (updated) => this.applyApiProfile(updated),
          });
        }
      })
    );
  }

  /**
   * Uploads a CV, optionally as the default CV.
   * @param file - CV file
   * @param asMain - Whether the CV becomes the default
   * @returns Observable of the updated profile
   */
  uploadCv(file: File, asMain = true): Observable<PlatformApiProfile> {
    return this.api
      .uploadDocument(file, 'cv', asMain)
      .pipe(tap((profile) => this.applyApiProfile(profile)));
  }

  /**
   * Uploads a CV and marks it as the main CV.
   * @param file - CV file
   * @returns Observable of the updated profile
   */
  uploadMainCv(file: File): Observable<PlatformApiProfile> {
    return this.uploadCv(file, true);
  }

  /**
   * Uploads an additional document.
   * @param file - Document file
   * @param type - Document type
   * @returns Observable of the updated profile
   */
  uploadDocument(
    file: File,
    type: 'cover-letter' | 'certificate' | 'other'
  ): Observable<PlatformApiProfile> {
    return this.api
      .uploadDocument(file, type, false)
      .pipe(tap((profile) => this.applyApiProfile(profile)));
  }

  /**
   * Uploads an additional document as type other.
   * @param file - Document file
   * @returns Observable of the updated profile
   */
  uploadOtherDocument(file: File): Observable<PlatformApiProfile> {
    return this.uploadDocument(file, 'other');
  }

  /**
   * Removes a document from the profile.
   * @param uploadId - Upload id
   * @returns Observable of the updated profile
   */
  deleteDocument(uploadId: number): Observable<PlatformApiProfile> {
    return this.api
      .deleteDocument(uploadId)
      .pipe(tap((profile) => this.applyApiProfile(profile)));
  }

  /**
   * Marks a CV as the main CV.
   * @param uploadId - Upload id
   * @returns Observable of the updated profile
   */
  setMainDocument(uploadId: number): Observable<PlatformApiProfile> {
    return this.api
      .setMainDocument(uploadId)
      .pipe(tap((profile) => this.applyApiProfile(profile)));
  }

  /**
   * Updates a profile document type and/or main-CV flag.
   * @param uploadId - Upload id
   * @param body - Type and/or main flag
   * @returns Observable of the updated profile
   */
  updateDocument(
    uploadId: number,
    body: { type?: 'cv' | 'cover-letter' | 'certificate' | 'other'; main?: boolean }
  ): Observable<PlatformApiProfile> {
    return this.api
      .updateDocument(uploadId, body)
      .pipe(tap((profile) => this.applyApiProfile(profile)));
  }

  /**
   * Loads a signed URL for document preview or download.
   * @param uploadId - Upload id
   * @returns Observable with the signed file URL
   */
  documentUrl(uploadId: number): Observable<string> {
    return this.api.documentUrl(uploadId).pipe(map((result) => result.url));
  }

  /**
   * Uploads the application photo.
   * @param file - Image file
   * @returns Observable of the updated profile
   */
  uploadPhoto(file: File): Observable<PlatformApiProfile> {
    return this.api.uploadPhoto(file).pipe(tap((profile) => this.applyApiProfile(profile)));
  }

  /**
   * Parses a CV into profile data and stores it as main CV.
   * @param file - CV file
   * @returns Observable of the extracted profile
   */
  parseCv(file: File) {
    return this.api.parseCv(file).pipe(
      tap(() => {
        this.reload().subscribe({ error: () => undefined });
        this.auth.refreshMe().subscribe({ error: () => undefined });
      })
    );
  }

  /**
   * Whether a missing-data type is already covered by the master profile.
   * @param type - Missing data type
   * @param profile - Current profile
   * @returns True when the profile already contains this information
   */
  coversMissingType(
    type: PlatformMissingDataType,
    profile: PlatformProfile = this.profile()
  ): boolean {
    switch (type) {
      case PlatformMissingDataType.SALARY:
        return profile.salaryMin != null || profile.salaryMax != null;
      case PlatformMissingDataType.START_DATE:
        return profile.startDate != null || profile.availability != null;
      case PlatformMissingDataType.DOCUMENTS:
        return profile.documents.length > 0;
      case PlatformMissingDataType.SKILLS:
        return profile.skills.length > 0;
      case PlatformMissingDataType.ISA:
        return profile.isaCompleted;
      default:
        return false;
    }
  }

  /**
   * Builds an empty profile for the given email.
   * @param email - Account email
   * @returns Empty profile
   */
  emptyProfile(email: string): PlatformProfile {
    return {
      firstName: '',
      lastName: '',
      title: Gender.DIVERSE,
      academicTitle: AcademicTitle.NONE,
      currentOccupation: '',
      email,
      phone: '',
      city: '',
      photoSrc: null,
      preferredLocations: [],
      workingModel: null,
      salaryMin: null,
      salaryMax: null,
      currency: 'EUR',
      availability: null,
      linkedin: '',
      xing: '',
      documents: [],
      pendingImportedCvs: [],
      skills: [],
      languages: [],
      documentParsingsRemaining: 5,
      startDate: null,
      isaCompleted: false,
    };
  }

  /**
   * Maps skill names to profile skills.
   * @param names - Skill names
   * @returns Skills with a default level
   */
  skillsFromNames(names: string[]): PlatformProfileSkill[] {
    return names
      .map((name) => name.trim())
      .filter((name) => name.length > 0)
      .map((name) => ({ name, level: ApplicantSkillLevel.GOOD }));
  }

  /**
   * Reloads CVs that can still be taken over from connected applications.
   */
  reloadImportedCvs(): void {
    if (!this.auth.loggedIn()) {
      this.pendingImportedCvsState.set([]);
      return;
    }
    this.api.importedCvs().subscribe({
      next: (cvs) => this.pendingImportedCvsState.set(this.toImportedCvs(cvs)),
      error: () => this.pendingImportedCvsState.set([]),
    });
  }

  /**
   * Applies an API profile to local state and syncs session identity.
   * @param profile - API profile
   */
  private applyApiProfile(profile: PlatformApiProfile): void {
    this.apiProfileState.set(profile);
    this.profileRequest = null;
    this.auth.patchSessionIdentity(
      profile.firstName,
      profile.lastName,
      profile.documents?.some((document) => document.main) ?? false
    );
  }

  /**
   * Maps an API profile to the profile used by the platform views.
   * @param profile - API profile
   * @param pendingImportedCvs - Imported CVs that still need a decision
   * @returns UI profile
   */
  private toUiProfile(
    profile: PlatformApiProfile,
    pendingImportedCvs: PlatformImportedCv[]
  ): PlatformProfile {
    return {
      firstName: profile.firstName,
      lastName: profile.lastName,
      title: profile.title ?? Gender.DIVERSE,
      academicTitle: profile.academicTitle ?? AcademicTitle.NONE,
      currentOccupation: profile.currentOccupation ?? '',
      email: profile.email,
      phone: profile.phone ?? '',
      city: profile.city ?? '',
      photoSrc: profile.photoUrl,
      preferredLocations: profile.preferredLocations ?? [],
      workingModel: profile.workingModel,
      salaryMin: profile.salaryMin,
      salaryMax: profile.salaryMax,
      currency: profile.currency ?? 'EUR',
      availability: profile.availability,
      linkedin: profile.linkedin ?? '',
      xing: profile.xing ?? '',
      documents: this.toDocuments(profile),
      pendingImportedCvs,
      skills: profile.skills ?? [],
      languages: profile.languages ?? [],
      documentParsingsRemaining: profile.documentParsingsRemaining,
      startDate:
        startDateFromIso(profile.startDate) ??
        startDateFromAvailability(profile.availability),
      isaCompleted: profile.isaCompleted,
    };
  }

  /**
   * Maps API documents to the UI document list.
   * @param profile - API profile
   * @returns Documents
   */
  private toDocuments(profile: PlatformApiProfile): PlatformDocument[] {
    return (profile.documents ?? []).map((document) => ({
      id: document.id,
      name: document.name,
      kind: document.main
        ? PlatformDocumentKind.MAIN_CV
        : document.type === 'cv'
          ? PlatformDocumentKind.CV
          : document.type === 'cover-letter'
            ? PlatformDocumentKind.COVER_LETTER
            : document.type === 'certificate'
              ? PlatformDocumentKind.CERTIFICATE
              : PlatformDocumentKind.OTHER,
      usedInApplications: document.usedInApplications ?? [],
    }));
  }

  /**
   * Maps imported CVs from the API.
   * @param cvs - API imported CVs
   * @returns UI imported CVs
   */
  private toImportedCvs(cvs: PlatformApiImportedCv[]): PlatformImportedCv[] {
    return cvs.map((cv) => ({
      id: cv.uploadId,
      name: cv.name,
      applicationId: cv.applicationId,
      action: PlatformImportedCvAction.UNSET,
    }));
  }

  /**
   * Builds the adopt-documents payload from decided imported CVs.
   * @param cvs - Imported CVs
   * @returns Uploads to take over
   */
  private adoptPayload(
    cvs: PlatformImportedCv[]
  ): {
    applicationId: number;
    uploadId: number;
    type: 'cv' | 'cover-letter' | 'certificate' | 'other';
    main?: boolean;
  }[] {
    return cvs
      .filter(
        (cv) =>
          cv.action !== PlatformImportedCvAction.UNSET &&
          cv.action !== PlatformImportedCvAction.DISCARD
      )
      .map((cv) => ({
        applicationId: cv.applicationId,
        uploadId: cv.id,
        type: this.importedActionToUploadType(cv.action),
        main: cv.action === PlatformImportedCvAction.MAIN,
      }));
  }

  /**
   * Maps an imported-CV action to the upload type sent to the API.
   * @param action - Imported CV action
   * @returns Upload type
   */
  private importedActionToUploadType(
    action: PlatformImportedCvAction
  ): 'cv' | 'cover-letter' | 'certificate' | 'other' {
    switch (action) {
      case PlatformImportedCvAction.MAIN:
      case PlatformImportedCvAction.CV:
        return 'cv';
      case PlatformImportedCvAction.COVER_LETTER:
        return 'cover-letter';
      case PlatformImportedCvAction.CERTIFICATE:
        return 'certificate';
      default:
        return 'other';
    }
  }
}
